import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Orgs, type OrgFiscal, type TaxIdType } from "../../../lib/apiTyped";
import { useApiCall } from "../../../lib/useToastError";

export type FiscalDraft = {
  pais: string;
  razonSocial: string;
  nif: string;
  taxIdType?: TaxIdType;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  region: string;
  emailFacturacion: string;
  consent: boolean;
};

type SubStep = "country" | "tax" | "address" | "email" | "consent";

const SUB_ORDER: readonly SubStep[] = [
  "country",
  "tax",
  "address",
  "email",
  "consent",
] as const;

const POPULAR_COUNTRIES: Array<[string, string]> = [
  ["ES", "España"],
  ["FR", "Francia"],
  ["DE", "Alemania"],
  ["IT", "Italia"],
  ["GB", "Reino Unido"],
  ["US", "Estados Unidos"],
];

const ALL_COUNTRIES: Array<[string, string]> = [
  ["ES", "España"],
  ["FR", "Francia"],
  ["DE", "Alemania"],
  ["IT", "Italia"],
  ["PT", "Portugal"],
  ["NL", "Países Bajos"],
  ["BE", "Bélgica"],
  ["IE", "Irlanda"],
  ["AT", "Austria"],
  ["LU", "Luxemburgo"],
  ["DK", "Dinamarca"],
  ["FI", "Finlandia"],
  ["SE", "Suecia"],
  ["GB", "Reino Unido"],
  ["CH", "Suiza"],
  ["NO", "Noruega"],
  ["US", "Estados Unidos"],
];

const TAX_ID_OPTIONS: Array<[TaxIdType, string]> = [
  ["es_cif", "CIF (España)"],
  ["es_dni", "DNI / NIF (España)"],
  ["eu_vat", "VAT europeo"],
  ["gb_vat", "VAT Reino Unido"],
  ["ch_vat", "VAT Suiza"],
  ["no_vat", "VAT Noruega"],
  ["us_ein", "EIN (EE. UU.)"],
];

function defaultTaxIdType(country: string): TaxIdType {
  if (country === "ES") return "es_cif";
  if (country === "GB") return "gb_vat";
  if (country === "CH") return "ch_vat";
  if (country === "US") return "us_ein";
  if (country === "NO") return "no_vat";
  return "eu_vat";
}

function isEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

type Props = {
  orgId: string;
  draft: FiscalDraft;
  onDraftChange: (draft: FiscalDraft) => void;
  consentRecorded: boolean;
  onSaved: () => void;
  onBack: () => void;
  onSkip: () => void;
};

export function StepFiscal({
  orgId,
  draft,
  onDraftChange,
  consentRecorded,
  onSaved,
  onBack,
  onSkip,
}: Props) {
  const [sub, setSub] = useState<SubStep>("country");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  function update<K extends keyof FiscalDraft>(key: K, value: FiscalDraft[K]) {
    onDraftChange({ ...draft, [key]: value });
  }

  function nextSub() {
    const idx = SUB_ORDER.indexOf(sub);
    if (idx < SUB_ORDER.length - 1) setSub(SUB_ORDER[idx + 1]);
  }

  function prevSub() {
    const idx = SUB_ORDER.indexOf(sub);
    if (idx > 0) setSub(SUB_ORDER[idx - 1]);
    else onBack();
  }

  async function save() {
    if (busy) return;
    const needsConsent = !consentRecorded;
    if (needsConsent && !draft.consent) return;
    setBusy(true);
    const fiscal: OrgFiscal = {
      nif: draft.nif.trim(),
      taxIdType: draft.taxIdType ?? defaultTaxIdType(draft.pais),
      razonSocial: draft.razonSocial.trim(),
      direccionFiscal: {
        line1: draft.line1.trim(),
        line2: draft.line2.trim() ? draft.line2.trim() : null,
        city: draft.city.trim(),
        postalCode: draft.postalCode.trim(),
        region: draft.region.trim() ? draft.region.trim() : null,
      },
      pais: draft.pais,
      emailFacturacion: draft.emailFacturacion.trim(),
    };
    const result = await run(() =>
      Orgs.updateFiscal(orgId, fiscal, {
        consent: needsConsent ? { version: "v1", accepted: true } : undefined,
      }),
    );
    setBusy(false);
    if (result) onSaved();
  }

  const subIdx = SUB_ORDER.indexOf(sub);

  return (
    <section className="space-y-10">
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm font-semibold uppercase tracking-wider text-ink-3">
            Paso 2 · Datos fiscales · {subIdx + 1}/{SUB_ORDER.length}
          </p>
          <button
            type="button"
            onClick={onSkip}
            className="text-xs font-semibold text-ink-3 hover:text-ink transition-colors cursor-pointer"
          >
            Saltar datos fiscales
          </button>
        </div>
        {sub === "country" && (
          <CountryStep
            value={draft.pais}
            onChange={(v) => {
              onDraftChange({
                ...draft,
                pais: v,
                taxIdType: defaultTaxIdType(v),
              });
            }}
            onContinue={nextSub}
            onBack={prevSub}
          />
        )}
        {sub === "tax" && (
          <TaxStep
            razonSocial={draft.razonSocial}
            nif={draft.nif}
            taxIdType={draft.taxIdType ?? defaultTaxIdType(draft.pais)}
            onRazonChange={(v) => update("razonSocial", v)}
            onNifChange={(v) => update("nif", v)}
            onTaxIdTypeChange={(v) => update("taxIdType", v)}
            onContinue={nextSub}
            onBack={prevSub}
          />
        )}
        {sub === "address" && (
          <AddressStep
            line1={draft.line1}
            line2={draft.line2}
            city={draft.city}
            postalCode={draft.postalCode}
            region={draft.region}
            onChange={(patch) => onDraftChange({ ...draft, ...patch })}
            onContinue={nextSub}
            onBack={prevSub}
          />
        )}
        {sub === "email" && (
          <EmailStep
            value={draft.emailFacturacion}
            onChange={(v) => update("emailFacturacion", v)}
            onContinue={nextSub}
            onBack={prevSub}
          />
        )}
        {sub === "consent" && (
          <ConsentStep
            consent={draft.consent}
            onConsentChange={(v) => update("consent", v)}
            consentRecorded={consentRecorded}
            busy={busy}
            onSave={save}
            onBack={prevSub}
          />
        )}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function StepHeader({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="space-y-2 mb-6">
      <h1 className="text-4xl sm:text-6xl font-black tracking-tight text-ink leading-[1.05]">
        {title}
      </h1>
      {hint && <p className="text-base sm:text-lg text-ink-2 max-w-xl">{hint}</p>}
    </div>
  );
}

function NavButtons({
  onBack,
  continueDisabled,
  continueLabel = "Continuar",
}: {
  onBack: () => void;
  continueDisabled?: boolean;
  continueLabel?: string;
}) {
  return (
    <div className="flex items-center gap-4 pt-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-sm text-ink-2 hover:text-ink transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Atrás
      </button>
      <button
        type="submit"
        disabled={continueDisabled}
        className="inline-flex items-center gap-2 bg-ink text-white text-base font-semibold px-6 py-3 rounded-full transition-all duration-200 hover:scale-[1.02] disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        {continueLabel}
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function BigInput({
  value,
  onChange,
  placeholder,
  type = "text",
  autoFocus,
  inputMode,
  spellCheck,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  autoFocus?: boolean;
  inputMode?: "text" | "email" | "numeric";
  spellCheck?: boolean;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  return (
    <input
      ref={ref}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      inputMode={inputMode}
      spellCheck={spellCheck}
      className="w-full bg-transparent text-2xl sm:text-4xl font-black tracking-tight text-ink placeholder:text-ink-3/40 border-0 border-b-2 pb-3 focus:outline-none transition-colors"
      style={{ borderBottomColor: "rgba(15,15,15,0.18)" }}
      onFocus={(e) => {
        e.currentTarget.style.borderBottomColor = "#0F0F0F";
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderBottomColor = "rgba(15,15,15,0.18)";
      }}
    />
  );
}

// ───────────────────────────────────────────────────────────────────────────

function CountryStep({
  value,
  onChange,
  onContinue,
  onBack,
}: {
  value: string;
  onChange: (v: string) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  const popularSlugs = POPULAR_COUNTRIES.map(([code]) => code);
  const showOther = !popularSlugs.includes(value);
  const [otherOpen, setOtherOpen] = useState(showOther);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onContinue();
      }}
    >
      <StepHeader
        title="¿Desde dónde facturas?"
        hint="Lo necesitamos para emitir tus facturas y configurar el IVA."
      />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {POPULAR_COUNTRIES.map(([code, name]) => {
          const active = value === code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => {
                onChange(code);
                setOtherOpen(false);
              }}
              className="px-4 py-4 rounded-2xl text-left transition-all duration-150"
              style={{
                backgroundColor: active ? "#0F0F0F" : "#FFFFFF",
                color: active ? "#FFFFFF" : "#0F0F0F",
                border: active
                  ? "1px solid #0F0F0F"
                  : "1px solid rgba(0,0,0,0.08)",
                transform: active ? "scale(1.02)" : "scale(1)",
              }}
            >
              <div className="text-xs font-semibold uppercase tracking-wider opacity-70">
                {code}
              </div>
              <div className="text-base font-bold mt-0.5">{name}</div>
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setOtherOpen(true)}
          className="px-4 py-4 rounded-2xl text-left transition-all duration-150"
          style={{
            backgroundColor: otherOpen ? "#F7F5F2" : "#FFFFFF",
            border: "1px solid rgba(0,0,0,0.08)",
            color: "#0F0F0F",
          }}
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Otro
          </div>
          <div className="text-base font-bold mt-0.5">Elegir país</div>
        </button>
      </div>

      {otherOpen && (
        <div className="mt-5">
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-xl px-4 py-3 text-base focus:outline-none"
            style={{
              backgroundColor: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(0,0,0,0.14)",
              color: "#0f0f0f",
            }}
          >
            {ALL_COUNTRIES.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      <NavButtons onBack={onBack} />
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function TaxStep({
  razonSocial,
  nif,
  taxIdType,
  onRazonChange,
  onNifChange,
  onTaxIdTypeChange,
  onContinue,
  onBack,
}: {
  razonSocial: string;
  nif: string;
  taxIdType: TaxIdType;
  onRazonChange: (v: string) => void;
  onNifChange: (v: string) => void;
  onTaxIdTypeChange: (v: TaxIdType) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  const [editType, setEditType] = useState(false);
  const valid = razonSocial.trim().length >= 2 && nif.trim().length >= 5;
  const taxLabel =
    TAX_ID_OPTIONS.find(([slug]) => slug === taxIdType)?.[1] ?? taxIdType;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onContinue();
      }}
    >
      <StepHeader
        title="¿Cómo se llama tu empresa?"
        hint="Razón social y número de identificación fiscal tal y como aparecen en tus papeles."
      />
      <div className="space-y-6">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Razón social
          </label>
          <BigInput
            value={razonSocial}
            onChange={onRazonChange}
            placeholder="Acme S.L."
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Número fiscal
          </label>
          <BigInput
            value={nif}
            onChange={onNifChange}
            placeholder="B12345678"
            spellCheck={false}
          />
          <div className="mt-2 text-sm text-ink-3 flex items-center gap-2">
            <span>Tipo: {taxLabel}</span>
            <button
              type="button"
              onClick={() => setEditType((s) => !s)}
              className="underline hover:text-ink"
            >
              cambiar
            </button>
          </div>
          {editType && (
            <select
              value={taxIdType}
              onChange={(e) => {
                onTaxIdTypeChange(e.target.value as TaxIdType);
                setEditType(false);
              }}
              className="mt-2 rounded-xl px-3 py-2 text-sm focus:outline-none"
              style={{
                backgroundColor: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(0,0,0,0.14)",
                color: "#0f0f0f",
              }}
            >
              {TAX_ID_OPTIONS.map(([slug, name]) => (
                <option key={slug} value={slug}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <NavButtons onBack={onBack} continueDisabled={!valid} />
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function AddressStep({
  line1,
  line2,
  city,
  postalCode,
  region,
  onChange,
  onContinue,
  onBack,
}: {
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  region: string;
  onChange: (patch: Partial<FiscalDraft>) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  const valid =
    line1.trim().length >= 3 &&
    city.trim().length >= 2 &&
    postalCode.trim().length >= 3;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onContinue();
      }}
    >
      <StepHeader
        title="¿A qué dirección facturamos?"
        hint="La que aparecerá impresa en tus facturas."
      />
      <div className="space-y-5">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Línea 1
          </label>
          <BigInput
            value={line1}
            onChange={(v) => onChange({ line1: v })}
            placeholder="Calle Mayor 12"
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
            Línea 2 (opcional)
          </label>
          <input
            type="text"
            value={line2}
            onChange={(e) => onChange({ line2: e.target.value })}
            placeholder="Piso, oficina, etc."
            className="w-full rounded-xl px-4 py-3 text-base focus:outline-none"
            style={{
              backgroundColor: "rgba(255,255,255,0.6)",
              border: "1px solid rgba(0,0,0,0.14)",
              color: "#0f0f0f",
            }}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field
            label="Ciudad"
            value={city}
            onChange={(v) => onChange({ city: v })}
            placeholder="Madrid"
          />
          <Field
            label="Código postal"
            value={postalCode}
            onChange={(v) => onChange({ postalCode: v })}
            placeholder="28001"
            inputMode="numeric"
          />
          <Field
            label="Región (opcional)"
            value={region}
            onChange={(v) => onChange({ region: v })}
            placeholder="Madrid"
          />
        </div>
      </div>

      <NavButtons onBack={onBack} continueDisabled={!valid} />
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: "text" | "numeric" | "email";
}) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        className="w-full rounded-xl px-4 py-3 text-base focus:outline-none"
        style={{
          backgroundColor: "rgba(255,255,255,0.6)",
          border: "1px solid rgba(0,0,0,0.14)",
          color: "#0f0f0f",
        }}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function EmailStep({
  value,
  onChange,
  onContinue,
  onBack,
}: {
  value: string;
  onChange: (v: string) => void;
  onContinue: () => void;
  onBack: () => void;
}) {
  const valid = isEmail(value.trim());
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) onContinue();
      }}
    >
      <StepHeader
        title="¿A qué email enviamos las facturas?"
        hint="Te las mandaremos automáticamente cada vez que se emita una."
      />
      <BigInput
        value={value}
        onChange={onChange}
        placeholder="facturas@empresa.com"
        type="email"
        inputMode="email"
        autoFocus
      />
      <NavButtons onBack={onBack} continueDisabled={!valid} />
    </form>
  );
}

// ───────────────────────────────────────────────────────────────────────────

function ConsentStep({
  consent,
  onConsentChange,
  consentRecorded,
  busy,
  onSave,
  onBack,
}: {
  consent: boolean;
  onConsentChange: (v: boolean) => void;
  consentRecorded: boolean;
  busy: boolean;
  onSave: () => void;
  onBack: () => void;
}) {
  const needsConsent = !consentRecorded;
  const canSave = !busy && (!needsConsent || consent);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) onSave();
      }}
    >
      <StepHeader
        title="Listos para guardar."
        hint="Validaremos tu número fiscal y crearemos tu cliente en Stripe Payments Europe. Solo tienes que confirmar."
      />
      {needsConsent && (
        <label className="flex items-start gap-3 cursor-pointer text-sm text-ink-2 max-w-xl mb-2">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => onConsentChange(e.target.checked)}
            className="mt-1 w-5 h-5 accent-ink shrink-0"
          />
          <span>
            Acepto que estos datos se traten conforme a la{" "}
            <Link
              to="/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-ink hover:opacity-80"
            >
              Política de Privacidad
            </Link>{" "}
            para emitir facturas y crear mi perfil de cliente en Stripe Payments
            Europe.
          </span>
        </label>
      )}
      <NavButtons
        onBack={onBack}
        continueDisabled={!canSave}
        continueLabel={busy ? "Guardando…" : "Guardar y continuar"}
      />
    </form>
  );
}
